/**
 * SupportMessagesTab — bell-dropdown tab listing replies received on the
 * user's open support tickets.
 *
 * Distinct from the "Notificações" tab (server-side platform events) and
 * the "Alertas" tab (deal alerts). Entries are sourced from
 * `useSupportNotificationsStore` (populated by `useNewSupportMessages`,
 * mounted in AppLayout).
 *
 * Clicking an entry navigates to the ticket detail page, marks the row as
 * read in the store, and writes the ticket's `lastMessageAt` into the
 * `support:lastSeen:{id}` localStorage mark so the detector won't re-flag
 * the same message after a reload.
 */

import React from 'react';
import { Button, Empty, List, Typography } from 'antd';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { MessageCircle } from 'lucide-react';
import {
  setLastSeen,
  useSupportNotificationsStore,
  type SupportNotificationEntry,
} from '../../stores/useSupportNotificationsStore';
import { supportTicketDetailUrl } from '../../../modules/support/routes';

const { Text } = Typography;

const BRAND_INDIGO = '#6366F1';

function formatTimeAgo(dateStr: string, locale: string): string {
  const now = new Date();
  const date = new Date(dateStr);
  const diffMs = now.getTime() - date.getTime();
  const diffMin = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMin / 60);
  const diffDays = Math.floor(diffHours / 24);
  if (diffMin < 1) return 'agora';
  if (diffMin < 60) return `há ${diffMin}min`;
  if (diffHours < 24) return `há ${diffHours}h`;
  if (diffDays < 7) return `há ${diffDays}d`;
  return date.toLocaleDateString(locale || 'pt-BR');
}

interface Props {
  onClose: () => void;
}

const SupportMessagesTab: React.FC<Props> = ({ onClose }) => {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const entries = useSupportNotificationsStore((s) => s.entries);
  const markRead = useSupportNotificationsStore((s) => s.markRead);
  const markAllRead = useSupportNotificationsStore((s) => s.markAllRead);
  const remove = useSupportNotificationsStore((s) => s.remove);

  const handleClick = (entry: SupportNotificationEntry) => {
    markRead(entry.id);
    // Persist the lastSeen mark so a reload doesn't reflash this message.
    const ts = Date.parse(entry.lastMessageAt);
    if (Number.isFinite(ts)) setLastSeen(entry.ticketId, ts);
    onClose();
    navigate(supportTicketDetailUrl(entry.ticketId));
  };

  const handleDismiss = (e: React.MouseEvent, entry: SupportNotificationEntry) => {
    e.stopPropagation();
    // Dismissing also acknowledges — bump lastSeen so the poller won't
    // resurrect the same entry on the next tick.
    const ts = Date.parse(entry.lastMessageAt);
    if (Number.isFinite(ts)) setLastSeen(entry.ticketId, ts);
    remove(entry.id);
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', maxHeight: 400 }}>
      <div
        style={{
          display: 'flex',
          justifyContent: 'flex-end',
          padding: '6px 12px',
          borderBottom: '1px solid #F1F5F9',
        }}
      >
        <Button
          type="link"
          size="small"
          onClick={() => markAllRead()}
          style={{ fontSize: 12, padding: 0, color: BRAND_INDIGO }}
        >
          {t('notifications.markAllAsRead', 'Marcar todas como lidas')}
        </Button>
      </div>
      <div style={{ overflow: 'auto', flex: 1 }}>
        {entries.length === 0 ? (
          <Empty
            image={Empty.PRESENTED_IMAGE_SIMPLE}
            description={t('notifications.empty', 'Nenhuma notificação por enquanto')}
            style={{ padding: '24px 0' }}
          />
        ) : (
          <List
            dataSource={entries}
            renderItem={(entry) => {
              const titleText = t('notifications.kinds.support_new_message', {
                shortId: entry.shortId,
                defaultValue: `Nova mensagem em ${entry.shortId}`,
              });
              const description = entry.bodyPreview
                ? `${entry.subject} — ${entry.bodyPreview}`
                : entry.subject;
              return (
                <List.Item
                  key={entry.id}
                  onClick={() => handleClick(entry)}
                  aria-label={titleText}
                  style={{
                    padding: '10px 16px',
                    cursor: 'pointer',
                    background: entry.read ? 'transparent' : '#F8FAFC',
                    borderBottom: '1px solid #F1F5F9',
                    alignItems: 'flex-start',
                  }}
                  actions={[
                    <Button
                      key="dismiss"
                      type="link"
                      size="small"
                      onClick={(e) => handleDismiss(e, entry)}
                      aria-label={t('notifications.dismiss', 'Dispensar')}
                      style={{ fontSize: 11, color: 'var(--color-text-tertiary)' }}
                    >
                      {t('notifications.dismiss', 'Dispensar')}
                    </Button>,
                  ]}
                >
                  <div style={{ display: 'flex', gap: 10, width: '100%', alignItems: 'flex-start' }}>
                    <span
                      aria-hidden="true"
                      style={{ color: BRAND_INDIGO, flexShrink: 0, marginTop: 2 }}
                    >
                      <MessageCircle size={14} />
                    </span>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8 }}>
                        <Text strong style={{ fontSize: 13 }}>
                          {titleText}
                        </Text>
                        <Text type="secondary" style={{ fontSize: 11, flexShrink: 0 }}>
                          {formatTimeAgo(entry.lastMessageAt, i18n.language)}
                        </Text>
                      </div>
                      <Text
                        type="secondary"
                        style={{
                          fontSize: 12,
                          display: '-webkit-box',
                          WebkitLineClamp: 2,
                          WebkitBoxOrient: 'vertical',
                          overflow: 'hidden',
                          lineHeight: '18px',
                        }}
                      >
                        {description}
                      </Text>
                    </div>
                  </div>
                </List.Item>
              );
            }}
          />
        )}
      </div>
    </div>
  );
};

export default SupportMessagesTab;
